"use client";

import { GeneralNewWindow } from "@fanduel/formation-react-icons";
import cx from "classnames";
import { groupBy, sortBy } from "lodash-es";
import { PropsWithChildren, useCallback, useMemo } from "react";
import Image from "@/components/Image";
import Link from "@/components/link/Link";
import OddsBtn, { useRedirectConfirmationModal } from "@/components/oddsBtn";
import { SPORTSBOOK_URL } from "@/constants/amplitude";
import { SCREENS } from "@/constants/breakpoints";
import { NFL } from "@/constants/sports";
import RedirectToSportsbook from "@/events/Modals/RedirectToSportsbookModal";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import useMedia from "@/utils/hooks/useMedia";
import { filterEmpty } from "@/utils/index";
import type {
  AlternateTotals,
  AnyTimeTouchdownScorer,
  EvenOddTotals,
  FirstTouchdownScorer,
  TeamTotals,
} from "../../types";
import styles from "./teamGameOdds.module.scss";

interface LabelsHeadingProps {
  composed?: boolean;
  clear?: boolean;
  leftLabel?: string;
  rightLabel?: string;
}

export function LabelsHeading({
  leftLabel = "Over",
  rightLabel = "Under",
  composed = false, // If true, renders "above" another element by adjusting borders and positioning
  clear = false, // If true, renders without any background color
}: LabelsHeadingProps = {}) {
  return (
    <div
      className={cx(styles.overUnderHeading, {
        [styles.composed]: composed,
        [styles.clear]: clear,
      })}
    >
      <div className={styles.spacer} />
      <div className={styles.labels}>
        <div className={styles.label}>{leftLabel}</div>
        <div className={styles.label}>{rightLabel}</div>
      </div>
    </div>
  );
}

export function LoneMarket({
  children,
  leftLabel,
  rightLabel,
}: PropsWithChildren<Pick<LabelsHeadingProps, "leftLabel" | "rightLabel">>) {
  return (
    <section className={styles.loneMarket}>
      <LabelsHeading leftLabel={leftLabel} rightLabel={rightLabel} />
      {children}
    </section>
  );
}

export function OverUnder({
  label,
  selections,
  showHandicap = false,
}: {
  label: string;
  selections: TeamTotals | AlternateTotals;
  showHandicap?: boolean;
}) {
  const sortedOverUnder = sortBy(selections, "formattedHandicap"); // ["O 211.5", "U 211.5"]
  const [overSelection, underSelection] = sortedOverUnder;

  const { odds: overOdds, handicap: overHandicap } = overSelection ?? {};
  const { odds: underOdds, handicap: underHandicap } = underSelection ?? {};

  const { eventName } = useHeadToHeadContext();
  const { onOddsClick, redirectToSportsbookProps } =
    useRedirectConfirmationModal(NFL, eventName, "team_game_odds");

  const overOddsClick = useCallback(
    () => onOddsClick(overSelection?.url ?? SPORTSBOOK_URL),
    [onOddsClick, overSelection?.url]
  );
  const underOddsClick = useCallback(
    () => onOddsClick(underSelection?.url ?? SPORTSBOOK_URL),
    [onOddsClick, underSelection?.url]
  );

  return (
    <div className={styles.marketBody}>
      <RedirectToSportsbook {...redirectToSportsbookProps} />
      <div className={styles.name}>{label}</div>
      <div className={styles.selections}>
        <OddsBtn
          classButton={styles.btn}
          odds={overOdds ?? ""}
          handicap={showHandicap ? overHandicap : undefined}
          onClick={overOddsClick}
          isSuspended={overSelection == null}
        />
        <OddsBtn
          classButton={styles.btn}
          odds={underOdds ?? ""}
          handicap={showHandicap ? underHandicap : undefined}
          onClick={underOddsClick}
          isSuspended={underSelection == null}
        />
      </div>
    </div>
  );
}

export function EvenOdd({
  label,
  selections,
}: {
  label: string;
  selections: EvenOddTotals;
}) {
  const sortedSelections = sortBy(selections, "runnerName"); // ["Even", "Odd"]
  const [evenSelection, oddSelection] = sortedSelections;

  const { odds: evenOdds } = evenSelection ?? {};
  const { odds: oddOdds } = oddSelection ?? {};

  const { eventName } = useHeadToHeadContext();
  const { onOddsClick, redirectToSportsbookProps } =
    useRedirectConfirmationModal(NFL, eventName, "team_game_odds");

  const evenOddsClick = useCallback(
    () => onOddsClick(evenSelection?.url ?? SPORTSBOOK_URL),
    [onOddsClick, evenSelection?.url]
  );
  const oddOddsClick = useCallback(
    () => onOddsClick(oddSelection?.url ?? SPORTSBOOK_URL),
    [onOddsClick, oddSelection?.url]
  );

  return (
    <div className={styles.marketBody}>
      <RedirectToSportsbook {...redirectToSportsbookProps} />
      <div className={styles.name}>{label}</div>
      <div className={styles.selections}>
        <OddsBtn
          classButton={styles.btn}
          odds={evenOdds ?? ""}
          onClick={evenOddsClick}
          isSuspended={evenSelection == null}
        />
        <OddsBtn
          classButton={styles.btn}
          odds={oddOdds ?? ""}
          onClick={oddOddsClick}
          isSuspended={oddSelection == null}
        />
      </div>
    </div>
  );
}

export function SingleMarket({
  selection,
}: {
  selection:
    | NonNullable<AnyTimeTouchdownScorer>[number]
    | NonNullable<FirstTouchdownScorer>[number];
}) {
  const runnerName = selection?.runnerName;
  const odds = selection?.odds;
  const url = selection?.url;

  const isSuspended = runnerName == null || odds == null || url == null;

  const { eventName } = useHeadToHeadContext();
  const { onOddsClick, redirectToSportsbookProps } =
    useRedirectConfirmationModal(NFL, eventName, "team_game_odds");

  return (
    <div className={styles.singleMarket}>
      <RedirectToSportsbook {...redirectToSportsbookProps} />
      {isSuspended ? (
        <div />
      ) : (
        <div className={styles.player}>
          <Image
            width={40}
            height={40}
            src={selection?.player?.teamInfo?.logo?.svg ?? ""}
            alt={(selection?.player?.teamInfo?.shortName ?? "Team") + " Logo"}
          />
          <span className={styles.playerName}>
            {selection?.player?.name ?? runnerName ?? ""}
          </span>
        </div>
      )}
      <div>
        <OddsBtn
          key={runnerName}
          classButton={styles.btn}
          odds={odds ?? ""}
          isSuspended={isSuspended}
          onClick={() => onOddsClick(url ?? SPORTSBOOK_URL)}
        />
      </div>
    </div>
  );
}

// For extracting "40.5" out of "Under (40.5)"
const TOTAL_EXTRACTION_REGEX = /\(([^()]*)\)/;

export function AlternateTotalPoints({
  markets,
  totalPointsValue,
}: {
  markets: AlternateTotals;
  totalPointsValue?: string | null;
}) {
  const isMultiColumnLayout = useMedia(`(min-width: ${SCREENS.MD})`);
  const { eventUrl } = useHeadToHeadContext();

  const marketList = useMemo(() => {
    // Group totals' over/under selections together
    const selectionsByTotal = groupBy(markets, (selections) => {
      const runnerName = selections?.runnerName ?? "";
      const [, total] = runnerName.match(TOTAL_EXTRACTION_REGEX) ?? [];
      return Number(total);
    });

    // List totals in ascending order
    const allTotals = Object.keys(selectionsByTotal)
      .map((total) => Number(total))
      .toSorted((a, b) => a - b);

    let totalsToRender: number[];
    if (allTotals.length <= 6) {
      // We can only render what we have
      totalsToRender = allTotals;
    } else if (totalPointsValue) {
      // We can render the 3 totals above and below the nominal totalPointsValue
      const totalPoints = Number(totalPointsValue);
      const availableTotals = allTotals.filter(
        (total) => total !== totalPoints // Exclude rendering the nominal totalPointsValue
      );
      const middleValue = availableTotals.findIndex(
        (total) => total > totalPoints
      );
      totalsToRender = availableTotals.slice(middleValue - 3, middleValue + 3);
    } else {
      // totalPointsValue is not available, so we take the middle-most 6 values as a reasonable fallback
      const middleIndex = Math.floor(allTotals.length / 2);
      const start = Math.max(0, middleIndex - 3);
      const end = Math.min(allTotals.length, middleIndex + 3);
      totalsToRender = allTotals.slice(start, end);
    }

    // Render with a simplified runnerName for display (Convert "Under (40.5)" -> "40.5")
    return totalsToRender.map((total) => {
      const totalStr = total.toString();
      const overUnderSelectionsForTotal = selectionsByTotal[total];
      const sortedSelections = sortBy(
        overUnderSelectionsForTotal,
        "formattedHandicap"
      ).map((selection) => ({
        ...selection,
        runnerName: totalStr, // Modify runnerName to be directly displayable
      }));

      return (
        <OverUnder key={total} label={totalStr} selections={sortedSelections} />
      );
    });
  }, [markets, totalPointsValue]);

  if (!isMultiColumnLayout) {
    let markets = marketList;
    if (marketList.length === 0) {
      // Render placeholder markets
      markets = [0, 1, 2].map((value) => (
        <OverUnder key={value} label={""} selections={[]} />
      ));
    }

    return (
      <section className={styles.bubble}>
        <div className={styles.title}>Alternate Total Points</div>
        <LabelsHeading composed clear />
        <div className={styles.marketList}>{markets}</div>
      </section>
    );
  }

  const half = Math.ceil(marketList.length / 2);
  let leftColumn = marketList.slice(0, half);
  let rightColumn = marketList.slice(half);

  if (leftColumn.length === 0 && rightColumn.length === 0) {
    // Render placeholder markets
    leftColumn = [0, 1, 2].map((value) => (
      <OverUnder key={value} label={""} selections={[]} />
    ));
    rightColumn = [0, 1, 2].map((value) => (
      <OverUnder key={value} label={""} selections={[]} />
    ));
  }

  return (
    <section className={styles.bubble}>
      <div className={styles.title}>Alternate Total Points</div>
      <div className={styles.columns}>
        <div>
          <LabelsHeading clear />
          <div className={styles.marketList}>{leftColumn}</div>
        </div>
        <div>
          <LabelsHeading clear />
          <div className={styles.marketList}>{rightColumn}</div>
        </div>
      </div>
      <div className={styles.alternateTotalsLink}>
        <Link href={eventUrl} target="_blank" next={false}>
          View more odds in Sportsbook
          <GeneralNewWindow />
        </Link>
      </div>
    </section>
  );
}

const PLAYERS_PER_TD_SCORER_MARKET = 6;

export function TouchdownScorerMarket({
  label,
  selections,
}: {
  label: string;
  selections: AnyTimeTouchdownScorer | FirstTouchdownScorer;
}) {
  let renderableSelections = filterEmpty(selections ?? []);
  renderableSelections = sortBy(renderableSelections, "sortPriority").slice(
    0,
    PLAYERS_PER_TD_SCORER_MARKET
  );

  const half = Math.ceil(renderableSelections.length / 2);
  let leftColumn = renderableSelections.slice(0, half);
  let rightColumn = renderableSelections.slice(half);

  if (leftColumn.length === 0 && rightColumn.length === 0) {
    // Render skeletons
    leftColumn = [0, 1, 2].map((selectionId) => ({ selectionId }));
    rightColumn = [0, 1, 2].map((selectionId) => ({ selectionId }));
  }

  return (
    <section className={styles.tdScorerMarket}>
      <div className={styles.marketName}>{label}</div>
      <div className={styles.subGroupings}>
        {leftColumn.length > 0 && (
          <div className={styles.selections}>
            {leftColumn.map((selection) => (
              <SingleMarket key={selection.selectionId} selection={selection} />
            ))}
          </div>
        )}
        {rightColumn.length > 0 && (
          <div className={styles.selections}>
            {rightColumn.map((selection) => (
              <SingleMarket key={selection.selectionId} selection={selection} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
